import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  CheckCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  MapPinIcon,
  ArrowLeftIcon,
  UserIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { useAppSelector, useAppDispatch } from "../../store";
import { selectUsers } from "../user/userSlice";
import {
  selectBranches,
  selectSelectedBranch,
  selectBranchLoading,
  selectBranchError,
  fetchBranches,
  fetchBranchById,
} from "./branchSlice";
import PageTransition from "../../components/common/PageTransition";

const BranchDetailsPage: React.FC = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const branches = useAppSelector(selectBranches);
  const selectedBranch = useAppSelector(selectSelectedBranch);
  const loading = useAppSelector(selectBranchLoading);
  const error = useAppSelector(selectBranchError);
  const users = useAppSelector(selectUsers);

  useEffect(() => {
    if (branchId) {
      dispatch(fetchBranchById(branchId));
    } else {
      dispatch(fetchBranches());
    }
  }, [dispatch, branchId]);

  // Use selectedBranch if available, otherwise find from branches list
  const branch = selectedBranch || branches.find((b) => b.id === branchId);

  const getBranchStats = () => {
    if (!branch) return null;
    const branchUsers = users.filter((user) => user.branchId === branch.id);
    // Note: These are placeholder values. You'll need to connect to actual order data
    return {
      totalUsers: branchUsers.length,
      pendingOrders: Math.floor(Math.random() * 50), // Replace with actual data
      totalOrders: Math.floor(Math.random() * 200), // Replace with actual data
      completedOrders: Math.floor(Math.random() * 150), // Replace with actual data
    };
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </PageTransition>
    );
  }

  if (error) {
    return (
      <PageTransition>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="mb-4">
                <BuildingOfficeIcon className="h-12 w-12 text-red-400 mx-auto" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Error Loading Branch
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
              <button
                onClick={() =>
                  branchId
                    ? dispatch(fetchBranchById(branchId))
                    : navigate("/branches")
                }
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                {branchId ? "Retry" : "Back to Branches"}
              </button>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  if (!branch) {
    return (
      <PageTransition>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="mb-4">
                <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                Branch not found
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                The branch you're looking for doesn't exist or you don't have
                access to it.
              </p>
              <button
                onClick={() => navigate("/branches")}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Branches
              </button>
            </div>
          </div>
        </div>
      </PageTransition>
    );
  }

  const stats = getBranchStats();

  const branchUsers = users.filter((user) => user.branchId === branch.id);
  const cnt = {
    totalUsers: branchUsers.length,
    activeUsers: branchUsers.filter((user) => user.status).length,
    inactiveUsers: branchUsers.filter((user) => !user.status).length,
  };

  const handleNavigateToUsers = (status: "all" | "active" | "inactive") => {
    navigate(`/users?branchId=${branch.id}&filterType=${status}`);
  };

  return (
    <PageTransition>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/branches")}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Branches
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                <BuildingOfficeIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  {branch.name}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">
                  Branch Code: {branch.code}
                </p>
              </div>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                branch.status
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                  : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
              }`}
            >
              {branch.status ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mb-6 flex items-center space-x-3">
          <ClipboardDocumentListIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Orders
          </h2>
        </div>
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          >
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Orders
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Pending Orders
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {stats.pendingOrders}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-green-500 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Completed Orders
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {stats.completedOrders}
                  </p>
                </div>
              </div>
            </div>

            {/* <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-purple-500 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Orders
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {stats.totalOrders}
                  </p>
                </div>
              </div>
            </div> */}
          </motion.div>
        )}

        <div className="mb-6 flex items-center space-x-3">
          <UserCircleIcon className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">
            Users
          </h2>
        </div>
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          >
            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6"
              onClick={() => handleNavigateToUsers("all")}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Users
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {cnt.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6"
              onClick={() => handleNavigateToUsers("active")}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-yellow-500 dark:text-yellow-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Active Users
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {cnt.activeUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6"
              onClick={() => handleNavigateToUsers("inactive")}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-green-500 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Inactive Users
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {cnt.inactiveUsers}
                  </p>
                </div>
              </div>
            </div>

            {/* <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardDocumentListIcon className="h-8 w-8 text-purple-500 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Orders
                  </p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {stats.totalOrders}
                  </p>
                </div>
              </div>
            </div> */}
          </motion.div>
        )}

        {/* Branch Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Contact Information */}
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Contact Information
            </h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Address
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.address}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.city}, {branch.state} {branch.zipCode}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Phone
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.phone}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Email
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.email}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Manager Information */}
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Manager Information
            </h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <UserGroupIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Manager Name
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.managerName}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Manager Phone
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.managerPhone}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Manager Email
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.managerEmail}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Working Hours
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {branch.workingHours.start} - {branch.workingHours.end}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </PageTransition>
  );
};

export default BranchDetailsPage;
